import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote, Star, PenLine } from "lucide-react";
import { TESTIMONIALS, PRACTICE } from "@/data/site";
import { useT } from "@/i18n/LanguageContext";
import { localizeTestimonial } from "@/i18n/data.es";
import useGoogleReviews from "@/hooks/useGoogleReviews";

/** Small Google "G" mark (public Google branding) */
function GoogleG({ className = "w-4 h-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 48 48"
      aria-hidden="true"
    >
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.5 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.4 13.7 17.7 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.5 3-2.2 5.5-4.7 7.1l7.4 5.7c4.3-4 6.9-9.9 6.9-17.3z" />
      <path fill="#FBBC05" d="M10.4 28.7c-.5-1.5-.8-3-.8-4.7s.3-3.2.8-4.7l-7.8-6.1C1 16.2 0 19.9 0 24s1 7.8 2.6 10.8l7.8-6.1z" />
      <path fill="#34A853" d="M24 48c6.2 0 11.4-2 15.2-5.5l-7.4-5.7c-2 1.4-4.6 2.2-7.8 2.2-6.3 0-11.6-4.2-13.6-10l-7.8 6.1C6.5 42.6 14.6 48 24 48z" />
    </svg>
  );
}

export default function Testimonials() {
  const { t, lang } = useT();
  const [i, setI] = useState(0);
  const total = TESTIMONIALS.length;

  useEffect(() => {
    const id = setInterval(() => setI((v) => (v + 1) % total), 8000);
    return () => clearInterval(id);
  }, [total]);

  const current = localizeTestimonial(TESTIMONIALS[i], lang);

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="px-6 md:px-10 lg:px-16 py-24 md:py-32"
    >
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-4">
          <div className="font-mono-eyebrow text-[var(--text-muted)] flex items-center gap-3">
            <span className="w-8 h-px bg-[var(--accent)]" />
            {t("What patients say")}
          </div>
          <h2 className="font-serif-display text-[var(--primary)] mt-5 text-[44px] md:text-[60px] leading-[1.02] tracking-tight">
            {t("Quiet words.")}<br />
            <span className="italic text-[var(--accent)]">{t("Loud")}</span> {t("smiles.")}
          </h2>

          {/* Google branded rating badge */}
          <a
            data-testid="google-rating-badge"
            href={PRACTICE.reviewsUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-6 inline-flex items-center gap-3 bg-white border border-[var(--border)] rounded-full py-2 px-4 hover:shadow-md transition-shadow"
          >
            <GoogleG className="w-5 h-5" />
            <div className="flex items-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, k) => (
                  <Star
                    key={k}
                    className="w-3.5 h-3.5 fill-[#FBBC05] text-[#FBBC05]"
                  />
                ))}
              </div>
              <span className="text-sm font-medium text-[var(--primary)]">
                {t("5.0 on Google")}
              </span>
            </div>
          </a>

          <p className="mt-4 text-sm text-[var(--text-muted)]">
            {t("Hundreds of verified reviews from real Leesburg families.")}
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <a
              data-testid="reviews-link"
              href={PRACTICE.reviewsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--bg)] transition-colors"
            >
              <GoogleG className="w-3.5 h-3.5" />
              {t("Read all on Google")}
            </a>
            <a
              data-testid="write-review-link"
              href={PRACTICE.googleWriteReviewUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] transition-colors"
            >
              <PenLine className="w-3.5 h-3.5" strokeWidth={2} />
              {t("Leave a review")}
            </a>
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="relative bg-[var(--surface)] border border-[var(--border)] rounded-[36px] p-8 md:p-14 min-h-[340px] flex flex-col justify-between overflow-hidden">
            <Quote
              className="absolute -top-3 -left-2 w-28 h-28 text-[var(--accent)]/15"
              strokeWidth={1}
            />
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={i}
                data-testid={`testimonial-${i}`}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.5 }}
                className="relative font-serif-display text-[24px] md:text-[32px] leading-[1.3] text-[var(--primary)] tracking-tight"
              >
                {/* Google-verified chip on each review */}
                <span className="inline-flex items-center gap-1.5 mb-4 px-2.5 py-1 rounded-full bg-white border border-[var(--border)] text-[11px] uppercase tracking-[0.18em] font-sans text-[var(--text-muted)]">
                  <GoogleG className="w-3 h-3" />
                  {t("Verified Google review")}
                </span>
                <div>
                  “{current.quote}”
                </div>
                <footer className="mt-6 font-sans text-sm uppercase tracking-[0.22em] text-[var(--text-muted)] flex items-center gap-2">
                  — {current.author}
                  <span className="text-[var(--accent)]">
                    {[..."★★★★★"].map((s, k) => (
                      <span key={k}>{s}</span>
                    ))}
                  </span>
                </footer>
              </motion.blockquote>
            </AnimatePresence>

            <div className="mt-8 flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                {TESTIMONIALS.map((tst, idx) => (
                  <button
                    key={tst.author}
                    data-testid={`testimonial-dot-${idx}`}
                    onClick={() => setI(idx)}
                    aria-label={`Show review ${idx + 1}`}
                    className={`h-1.5 rounded-full transition-all ${
                      idx === i ? "bg-[var(--accent)] w-8" : "bg-[var(--border)] w-3"
                    }`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="testimonial-prev"
                  onClick={() => setI((v) => (v - 1 + total) % total)}
                  className="w-11 h-11 rounded-full border border-[var(--border)] grid place-content-center hover:bg-[var(--primary)] hover:text-[var(--bg)]"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  data-testid="testimonial-next"
                  onClick={() => setI((v) => (v + 1) % total)}
                  className="w-11 h-11 rounded-full border border-[var(--border)] grid place-content-center hover:bg-[var(--primary)] hover:text-[var(--bg)]"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
